import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FounderSection = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isManagerVideoPlaying, setIsManagerVideoPlaying] = useState(false);

  const founderData = {
    name: "MR DERANGULA MALLIKARJUNA",
    title: "Founder & CEO",
    image: "/assets/images/aa-1762317510715.jpg",
    imageAlt: "Professional photo of MR DERANGULA MALLIKARJUNA, founder and CEO standing confidently in business attire",
    quote: "Every drop of water deserves respect. Every choice we make should honor our planet. Mava exists to prove that premium quality and environmental consciousness aren\'t just compatible—they\'re inseparable.",
    background: "Visionary entrepreneur with extensive experience in sustainable business development and environmental technology. Leading the transformation in how India approaches premium hydration solutions.",
    achievements: [
      "Sustainable Business Pioneer Award",
      "Environmental Innovation Leader 2023",
      "Green Technology Excellence Award"
    ]
  };

  const managerData = {
    name: "VAMSI KRISHNA",
    title: "General Manager & Strategic Operations",
    image: "/assets/images/bb-1762317997884.jpg",
    imageAlt: "Professional photo of VAMSI KRISHNA, general manager with black hair and beard wearing green and white checkered shirt",
    quote: "Excellence in execution transforms visionary ideas into tangible impact. At Mava, we bridge innovation with operational excellence to deliver sustainable solutions that truly matter.",
    background: "Strategic operations leader with deep expertise in scaling sustainable businesses and implementing innovative technologies. Driving operational excellence while maintaining our commitment to environmental responsibility.",
    achievements: [
      "Operations Excellence Award 2023",
      "Sustainability Implementation Leader",
      "Strategic Growth Achievement Award"
    ]
  };

  const teamMembers = [
  {
    name: "Priya Sharma",
    role: "Chief Technology Officer", 
    image: "/assets/images/mava-1762174454410.jpg",
    imageAlt: "Mava logo representing professional team member in leadership position with advanced technology expertise",
    expertise: "IoT & Smart Materials",
    experience: "12+ years"
  },
  {
    name: "Rajesh Kumar",
    role: "Head of Sustainability",
    image: "/assets/images/mava-1762174454410.jpg",
    imageAlt: "Mava logo representing professional team member specializing in environmental science and sustainability initiatives",
    expertise: "Environmental Science", 
    experience: "18+ years"
  },
  {
    name: "Sneha Patel",
    role: "Design Director",
    image: "/assets/images/mava-1762174454410.jpg",
    imageAlt: "Mava logo representing professional team member with expertise in product design and user experience",
    expertise: "Product Design & UX",
    experience: "10+ years"
  }];


  const visionPoints = [
  {
    icon: "Globe",
    title: "Global Impact",
    description: "Expanding sustainable hydration solutions to 50+ countries by 2030"
  },
  {
    icon: "Lightbulb",
    title: "Innovation Leadership",
    description: "Pioneering breakthrough technologies that redefine industry standards"
  },
  {
    icon: "Heart",
    title: "Community First",
    description: "Building a movement of conscious consumers who prioritize planet and health"
  }];


  return (
    <section className="py-20 bg-gradient-to-b from-background to-surface">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16">

            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-secondary to-primary rounded-lg flex items-center justify-center">
                <Icon name="Users" size={24} color="white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-poppins font-bold text-text-primary">
                Leadership Vision
              </h2>
            </div>
            <p className="text-lg text-text-secondary font-inter max-w-3xl mx-auto leading-relaxed">
              Meet the visionaries behind Mava's mission to transform how the world thinks about hydration and sustainability.
            </p>
          </motion.div>

          {/* Founder & Manager Spotlight */}
          <div className="space-y-20 mb-20">
            {/* Founder Spotlight */}
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-8">

                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden liquid-shadow">
                      <Image
                        src="/assets/images/mava-1762174454410.jpg"
                        alt="Mava logo representing MR DERANGULA MALLIKARJUNA, founder and CEO with leadership expertise"
                        className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-poppins font-bold text-text-primary">
                        MR DERANGULA MALLIKARJUNA
                      </h3>
                      <p className="text-primary font-inter font-medium">
                        Founder & CEO
                      </p>
                    </div>
                  </div>

                  <blockquote className="text-lg text-text-primary font-inter leading-relaxed italic border-l-4 border-primary pl-6">
                    "{founderData?.quote}"
                  </blockquote>

                  <p className="text-text-secondary font-inter leading-relaxed">
                    {founderData?.background}
                  </p>
                </div>

                {/* Achievements */}
                <div className="space-y-4">
                  <h4 className="font-poppins font-semibold text-text-primary">Recognition & Awards</h4>
                  <div className="space-y-2">
                    {founderData?.achievements?.map((achievement, index) =>
                    <div key={index} className="flex items-center space-x-3">
                        <Icon name="Award" size={16} color="var(--color-primary)" />
                        <span className="text-text-secondary font-inter text-sm">
                          {achievement}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Message */}
                <div className="bg-surface rounded-xl p-6 liquid-shadow">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <h4 className="font-poppins font-semibold text-text-primary">
                        Founder's Message
                      </h4>
                      <p className="text-text-secondary font-inter text-sm">
                        Personal insights on our mission and vision
                      </p>
                    </div>
                    <Button
                      variant={isVideoPlaying ? "outline" : "default"}
                      size="sm"
                      iconName={isVideoPlaying ? "Pause" : "Play"}
                      iconPosition="left"
                      onClick={() => setIsVideoPlaying(!isVideoPlaying)}>
                      {isVideoPlaying ? "Pause" : "Play"}
                    </Button>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative">

                <div className="relative w-full h-96 lg:h-[500px] rounded-2xl overflow-hidden liquid-shadow">
                  <Image
                    src="/assets/images/aa-1762317510715.jpg"
                    alt="Professional photo of a middle-aged man in a light-colored collared shirt standing in front of residential buildings"
                    className="w-full h-full object-cover" />

                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/30 via-transparent to-transparent" />
                  
                  {/* Leadership Stats */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="absolute top-6 right-6 bg-background/95 backdrop-blur-md rounded-xl p-4">
                    <div className="text-center">
                      <div className="text-2xl font-poppins font-bold text-secondary">15+</div>
                      <div className="text-sm text-text-secondary font-inter">Years Experience</div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                    className="absolute bottom-6 left-6 bg-background/95 backdrop-blur-md rounded-xl p-4">
                    <div className="text-center">
                      <div className="text-2xl font-poppins font-bold text-primary">₹50Cr+</div>
                      <div className="text-sm text-text-secondary font-inter">Impact Created</div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Manager Spotlight */}
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:order-2 space-y-8">

                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden liquid-shadow">
                      <Image
                        src="/assets/images/mava-1762174454410.jpg"
                        alt="Mava logo representing VAMSI KRISHNA, general manager with strategic operations expertise"
                        className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-poppins font-bold text-text-primary">
                        VAMSI KRISHNA
                      </h3>
                      <p className="text-secondary font-inter font-medium">
                        General Manager & Strategic Operations
                      </p>
                    </div>
                  </div>

                  <blockquote className="text-lg text-text-primary font-inter leading-relaxed italic border-l-4 border-secondary pl-6">
                    "{managerData?.quote}"
                  </blockquote>

                  <p className="text-text-secondary font-inter leading-relaxed">
                    {managerData?.background}
                  </p>
                </div>

                {/* Achievements */}
                <div className="space-y-4">
                  <h4 className="font-poppins font-semibold text-text-primary">Recognition & Awards</h4>
                  <div className="space-y-2">
                    {managerData?.achievements?.map((achievement, index) =>
                    <div key={index} className="flex items-center space-x-3">
                        <Icon name="Award" size={16} color="var(--color-secondary)" />
                        <span className="text-text-secondary font-inter text-sm">
                          {achievement}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Message */}
                <div className="bg-surface rounded-xl p-6 liquid-shadow">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <h4 className="font-poppins font-semibold text-text-primary">
                        Manager's Insights
                      </h4>
                      <p className="text-text-secondary font-inter text-sm">
                        Strategic operations and sustainable growth
                      </p>
                    </div>
                    <Button
                      variant={isManagerVideoPlaying ? "outline" : "secondary"}
                      size="sm"
                      iconName={isManagerVideoPlaying ? "Pause" : "Play"}
                      iconPosition="left"
                      onClick={() => setIsManagerVideoPlaying(!isManagerVideoPlaying)}>
                      {isManagerVideoPlaying ? "Pause" : "Play"}
                    </Button>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="lg:order-1 relative">

                <div className="relative w-full h-96 lg:h-[500px] rounded-2xl overflow-hidden liquid-shadow">
                  <Image
                    src="/assets/images/bb-1762317997884.jpg"
                    alt="Professional photo of a young man with black hair and beard wearing a green and white checkered shirt, taken in an indoor setting"
                    className="w-full h-full object-cover" />

                  <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent" />
                  
                  {/* Management Stats */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="absolute top-6 right-6 bg-background/95 backdrop-blur-md rounded-xl p-4">
                    <div className="text-center">
                      <div className="text-2xl font-poppins font-bold text-primary">12+</div>
                      <div className="text-sm text-text-secondary font-inter">Years Leadership</div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                    className="absolute bottom-6 left-6 bg-background/95 backdrop-blur-md rounded-xl p-4">
                    <div className="text-center">
                      <div className="text-2xl font-poppins font-bold text-secondary">95%</div>
                      <div className="text-sm text-text-secondary font-inter">Efficiency Rate</div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Leadership Synergy Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-20">
            
            <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-3xl p-8 md:p-12 liquid-shadow">
              <div className="text-center space-y-6">
                <div className="flex items-center justify-center space-x-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="Handshake" size={24} color="white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-poppins font-bold text-text-primary">
                    Leadership Synergy
                  </h3>
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                    <Icon name="Target" size={24} color="white" />
                  </div>
                </div>
                
                <p className="text-lg text-text-primary font-inter max-w-4xl mx-auto leading-relaxed">
                  Together, <strong>MR DERANGULA MALLIKARJUNA</strong> and <strong>VAMSI KRISHNA</strong> form a dynamic leadership duo that combines 
                  visionary thinking with operational excellence. Their collaborative approach ensures that Mava's innovative 
                  sustainable solutions are not just conceptualized but successfully brought to market with precision and impact.
                </p>
                
                <div className="grid md:grid-cols-2 gap-8 mt-8">
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mx-auto">
                      <Icon name="Lightbulb" size={24} color="white" />
                    </div>
                    <h4 className="font-poppins font-bold text-text-primary">Strategic Vision</h4>
                    <p className="text-text-secondary font-inter">
                      Pioneering sustainable innovation with clear market direction and environmental impact goals
                    </p>
                  </div>
                  
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 bg-secondary rounded-xl flex items-center justify-center mx-auto">
                      <Icon name="Settings" size={24} color="white" />
                    </div>
                    <h4 className="font-poppins font-bold text-text-primary">Operational Excellence</h4>
                    <p className="text-text-secondary font-inter">
                      Transforming innovative concepts into scalable, efficient operations that deliver real-world results
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Leadership Team */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-20">

            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-poppins font-bold text-text-primary mb-4">
                Leadership Team
              </h3>
              <p className="text-text-secondary font-inter max-w-2xl mx-auto">
                Experienced leaders driving innovation and sustainability across all aspects of our business
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {teamMembers?.map((member, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-background rounded-2xl p-6 liquid-shadow hover:shadow-lg brand-transition group text-center">

                  <div className="space-y-4">
                    <div className="relative w-24 h-24 mx-auto">
                      <div className="w-full h-full rounded-full overflow-hidden liquid-shadow group-hover:scale-105 brand-transition">
                        <Image
                        src={member?.image}
                        alt={member?.imageAlt}
                        className="w-full h-full object-cover" />

                      </div>
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="CheckCircle" size={16} color="white" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-poppins font-bold text-text-primary">
                        {member?.name}
                      </h4>
                      <p className="text-primary font-inter font-medium text-sm">
                        {member?.role}
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-center space-x-2">
                        <Icon name="Briefcase" size={14} color="var(--color-text-secondary)" />
                        <span className="text-text-secondary font-inter text-sm">
                          {member?.expertise}
                        </span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <Icon name="Clock" size={14} color="var(--color-text-secondary)" />
                        <span className="text-text-secondary font-inter text-sm">
                          {member?.experience}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Vision & Future */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-12">

            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-poppins font-bold text-text-primary mb-4">
                Our Vision for Tomorrow
              </h3>
              <p className="text-text-secondary font-inter max-w-3xl mx-auto leading-relaxed">
                We're not just building a company—we're cultivating a movement that will reshape how humanity approaches hydration, sustainability, and conscious consumption.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {visionPoints?.map((point, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-gradient-to-br from-surface to-background rounded-2xl p-8 liquid-shadow hover:shadow-lg brand-transition text-center">

                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-secondary to-primary rounded-xl flex items-center justify-center mx-auto">
                      <Icon name={point?.icon} size={24} color="white" />
                    </div>
                    <h4 className="text-xl font-poppins font-bold text-text-primary">
                      {point?.title}
                    </h4>
                    <p className="text-text-secondary font-inter leading-relaxed">
                      {point?.description}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );

};

export default FounderSection;